import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import ArtworkCard from '../components/ArtworkCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default hero image (fallback)
const DEFAULT_HERO_IMAGE = "https://static.wixstatic.com/media/11ba4a_309a85662f7245a4947f8868c19fc291~mv2_d_4294_2174_s_2.jpg/v1/fill/w_1200,h_800,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/11ba4a_309a85662f7245a4947f8868c19fc291~mv2_d_4294_2174_s_2.jpg";

const Home = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch hero image setting
        try {
          const siteRes = await axios.get(`${API}/settings/site`);
          if (siteRes.data.hero_image_url) {
            setHeroImage(siteRes.data.hero_image_url);
          }
        } catch (e) {
          console.log('Using default hero image');
        }

        // Try to get featured artworks first
        const featuredRes = await axios.get(`${API}/artworks/featured`);
        if (featuredRes.data.length > 0) {
          setFeaturedArtworks(featuredRes.data);
        } else {
          // Fallback to first 3 artworks if none featured
          const response = await axios.get(`${API}/artworks`);
          setFeaturedArtworks(response.data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching artworks:', error);
        // Fallback to regular artworks endpoint
        try {
          const response = await axios.get(`${API}/artworks`);
          setFeaturedArtworks(response.data.slice(0, 3));
        } catch (e) {
          console.error('Error fetching fallback artworks:', e);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-black" data-testid="home-page">
      {/* Hero Section - Dynamic height based on content */}
      <section className="relative flex items-center bg-black pt-20 min-h-[70vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Text Content */}
            <div className="animate-fade-in lg:sticky lg:top-32">
              <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-6">
                Public Art by Jim Richardson
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-tight mb-8">
                Stone on Glass
              </h1>
              <p className="text-white/70 text-lg leading-relaxed mb-10 max-w-lg">
                Art designed for public spaces. Modern, simple, clean, yet warm 
                because of the natural stone. Each piece tells a story of balance 
                between earth and transparency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/portfolio" data-testid="hero-portfolio-btn">
                  <Button className="btn-primary w-full sm:w-auto">
                    View Portfolio
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact" data-testid="hero-contact-btn">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto">
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image - Dynamic size based on actual image dimensions */}
            <div className="relative animate-slide-up flex items-center justify-center">
              <div className="relative bg-black shadow-soft overflow-hidden border border-white/10 inline-block">
                <img
                  src={heroImage}
                  alt="Stone on Glass Art by Jim Richardson"
                  className="block max-w-full h-auto"
                  style={{ maxHeight: '80vh' }}
                  data-testid="hero-image"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-teal-500/10 -z-10" />
              <div className="absolute -top-6 -right-6 w-24 h-24 border-2 border-teal-500/20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Works Section */}
      <section className="section-padding bg-neutral-950" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-4">
              Selected Works
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              Featured Artworks
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" data-testid="loading-spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredArtworks.map((artwork) => (
                <ArtworkCard key={artwork.id} artwork={artwork} />
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/portfolio" data-testid="view-all-btn">
              <Button variant="outline" className="btn-secondary">
                View All Works
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Artist Statement Section */}
      <section className="section-padding bg-black" data-testid="statement-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-teal-400 uppercase tracking-[0.3em] text-xs font-medium mb-6">
            The Artist
          </p>
          <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white italic leading-relaxed mb-8">
            "I love public art. I want my art to hang in public places."
          </blockquote>
          <p className="text-white/60 leading-relaxed mb-8 max-w-2xl mx-auto">
            Just stones on glass, suspended over a board. The stones are mostly from 
            Dallas Road beach near Cook Street in Victoria BC, Canada. Some of the 
            glass pieces were found objects.
          </p>
          <Link to="/about" data-testid="read-more-btn">
            <Button variant="outline" className="btn-secondary">
              Read More About Jim
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-black mb-6">
            Looking for Art for Your Space?
          </h2>
          <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">
            If you know of a public space that would be improved by some art, 
            let me know.
          </p>
          <Link to="/contact" data-testid="cta-contact-btn">
            <Button className="bg-black text-white hover:bg-neutral-900 px-8 py-3 rounded-sm uppercase tracking-widest text-xs font-semibold transition-all">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
